/**
 * A person's taste — the styles, colors and brands picked for them in the Gift quiz
 * (steps 08–10). One rule decides whether a person is "tuned" (T-132): the People card,
 * the quiz step 07b "Use Anna's taste?" and the "Personalized for" selector all read it.
 *
 * People (`/user-profile/profiles`) has no taste fields yet (BE-202): the taste lives in
 * the browser under the gift quiz's old key, so answers given before T-132 are kept.
 * Swapping the source for the API changes `readAllPersonTaste` / `writeAllPersonTaste`
 * (or the store that calls them), not the UI.
 */
import type { AvailableBrand, AvailableColor, AvailableStyle } from '@/types/my-style'
import type { FairTastePreviewItem } from '@/ui/tokens/fair-taste-preview.data'

export interface PersonTaste {
  styleIds: number[]
  colorIds: number[]
  brandIds: number[]
}

export type PersonTasteKind = 'styles' | 'colors' | 'brands'

export function emptyPersonTaste(): PersonTaste {
  return { styleIds: [], colorIds: [], brandIds: [] }
}

/** T-132: tuned = at least one style, color or brand is saved. */
export function isPersonTuned(taste: Partial<PersonTaste> | null | undefined): boolean {
  if (!taste) return false
  return Boolean(taste.styleIds?.length || taste.colorIds?.length || taste.brandIds?.length)
}

function ids(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  const out: number[] = []
  for (const item of value) {
    const id = Number(item)
    if (Number.isFinite(id) && !out.includes(id)) out.push(id)
  }
  return out
}

/** Whatever is stored (an older shape, strings, duplicates) becomes a clean taste. */
export function normalizePersonTaste(raw: unknown): PersonTaste {
  const value = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  return { styleIds: ids(value.styleIds), colorIds: ids(value.colorIds), brandIds: ids(value.brandIds) }
}

/** Partial update: quiz steps save one axis at a time (T-132: 08–10 are saved as they go). */
export function patchPersonTaste(base: PersonTaste | null | undefined, patch: Partial<PersonTaste>): PersonTaste {
  const current = base ?? emptyPersonTaste()
  return normalizePersonTaste({
    styleIds: patch.styleIds ?? current.styleIds,
    colorIds: patch.colorIds ?? current.colorIds,
    brandIds: patch.brandIds ?? current.brandIds,
  })
}

/* ── Source: localStorage until BE-202 ─────────────────────────────────── */

/** BE-202: `false` while People cannot store taste; the store reads the browser. */
export const PERSON_TASTE_FROM_API = false

/** Kept from the gift quiz (BE-153 era) so earlier answers survive. */
export const PERSON_TASTE_STORAGE_KEY = 'fair.gift.personTaste'

export type PersonTasteMap = Record<string, PersonTaste>

export function readAllPersonTaste(): PersonTasteMap {
  if (typeof window === 'undefined') return {}
  try {
    const raw = JSON.parse(window.localStorage.getItem(PERSON_TASTE_STORAGE_KEY) ?? '{}') as Record<string, unknown>
    const out: PersonTasteMap = {}
    for (const [id, taste] of Object.entries(raw ?? {})) out[id] = normalizePersonTaste(taste)
    return out
  } catch {
    return {}
  }
}

export function writeAllPersonTaste(map: PersonTasteMap): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PERSON_TASTE_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Private mode / quota — the taste lives until a reload.
  }
}

/* ── Resolver: ids → what the Taste preview draws ──────────────────────── */

export interface PersonTasteCatalog {
  styles: AvailableStyle[]
  colors: AvailableColor[]
  brands: AvailableBrand[]
}

export interface ResolvedPersonTaste {
  styles: FairTastePreviewItem[]
  colors: FairTastePreviewItem[]
  brands: FairTastePreviewItem[]
}

function pick<T extends { id: number }>(
  list: T[],
  wanted: number[],
  toItem: (entry: T) => FairTastePreviewItem,
): FairTastePreviewItem[] {
  const byId = new Map(list.map((entry) => [entry.id, entry]))
  return wanted.flatMap((id) => {
    const entry = byId.get(id)
    return entry ? [toItem(entry)] : []
  })
}

/** Keeps the pick order; ids missing from the catalogue (renamed, retired) are skipped. */
export function resolvePersonTaste(
  taste: PersonTaste | null | undefined,
  catalog: PersonTasteCatalog,
): ResolvedPersonTaste {
  const value = taste ?? emptyPersonTaste()
  return {
    styles: pick(catalog.styles, value.styleIds, (style) => ({ id: style.id, name: style.name, image: style.image })),
    colors: pick(catalog.colors, value.colorIds, (color) => ({ id: color.id, name: color.name, hex: color.hex })),
    brands: pick(catalog.brands, value.brandIds, (brand) => ({ id: brand.id, name: brand.name, image: brand.logo })),
  }
}
